import type { GettingStartedStep, Waiting } from '@myrewards/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { mySettingsQuery, updateMySettings, waitingQuery } from '../api';
import { useToast } from '../ui';
import { gettingStartedStep } from './waiting';

/**
 * Dismisses a getting-started step from Waiting on you (K25: "until done or dismissed"). The
 * choice is saved with the person's settings, audited, and undone from My preferences; the row and
 * the bell's count go at once. Other rows aren't dismissible and are left alone.
 */
export function useDismissStep(waiting: Waiting): (id: string) => void {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useCallback(
    (id: string) => {
      const step = gettingStartedStep(id, waiting.items);
      if (step === null) return;
      void (async () => {
        queryClient.setQueryData(waitingQuery.queryKey, (current) =>
          current ? { ...current, items: current.items.filter((i) => i.id !== id) } : current,
        );
        try {
          const mine = await queryClient.query(mySettingsQuery);
          const dismissed = new Set<GettingStartedStep>(mine.gettingStarted?.dismissed ?? []);
          dismissed.add(step);
          const saved = await updateMySettings({ gettingStarted: { dismissed: [...dismissed] } });
          queryClient.setQueryData(mySettingsQuery.queryKey, saved);
          toast.show({
            tone: 'done',
            title: 'Dismissed',
            body: 'You can bring it back from My preferences.',
          });
        } catch {
          toast.show({
            tone: 'problem',
            title: 'That wasn’t dismissed',
            body: 'It’s still in Waiting on you. Try again in a moment.',
          });
        } finally {
          await queryClient.invalidateQueries({ queryKey: waitingQuery.queryKey });
        }
      })();
    },
    [queryClient, toast, waiting.items],
  );
}
